import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toCurrency } from "@helpers";
import { useChartColors } from "@hooks";
import { AssetClass } from "@api/v1/hooks/performance/assetClasses";

const calculatePercentOfTotal = ({
  holdingsValue = 0,
  fundsValue = 0,
  etfValue = 0,
  derivativeValue = 0,
  bondsValue = 0,
  cryptoValue = 0,
  currenciesValue = 0,
  preciousMetalsValue = 0,
}: {
  holdingsValue?: number;
  fundsValue?: number;
  etfValue?: number;
  derivativeValue?: number;
  bondsValue?: number;
  cryptoValue?: number;
  currenciesValue?: number;
  preciousMetalsValue?: number;
}) => {
  const totalValue =
    holdingsValue +
      fundsValue +
      etfValue +
      derivativeValue +
      bondsValue +
      cryptoValue +
      currenciesValue +
      preciousMetalsValue || 1;
  return {
    holdings: Math.round((holdingsValue * 100) / totalValue),
    funds: Math.round((fundsValue * 100) / totalValue),
    etf: Math.round((etfValue * 100) / totalValue),
    derivative: Math.round((derivativeValue * 100) / totalValue),
    bonds: Math.round((bondsValue * 100) / totalValue),
    crypto: Math.round((cryptoValue * 100) / totalValue),
    currencies: Math.round((currenciesValue * 100) / totalValue),
    preciousMetals: Math.round((preciousMetalsValue * 100) / totalValue),
  };
};

interface AssetTypeStatistics {
  portfolioValue?: number;
  changeYTD?: number;
  changeTotalAbsolute?: number;
  hasData?: boolean;
}

interface UseAssetTypeDataParams {
  allHoldings: AssetTypeStatistics;
  funds: AssetTypeStatistics;
  etfs: AssetTypeStatistics;
  derivative: AssetTypeStatistics;
  bonds: AssetTypeStatistics;
  crypto: AssetTypeStatistics;
  currencies: AssetTypeStatistics;
  preciousMetals: AssetTypeStatistics;
  currency?: string;
}

export const useAssetTypeData = ({
  allHoldings,
  funds,
  etfs,
  derivative,
  bonds,
  crypto,
  currencies,
  preciousMetals,
  currency = "EUR",
}: UseAssetTypeDataParams) => {
  const { t } = useTranslation();
  const chartColors = useChartColors("securitiesChart");

  const percents = calculatePercentOfTotal({
    holdingsValue: allHoldings?.portfolioValue,
    fundsValue: funds?.portfolioValue,
    etfValue: etfs?.portfolioValue,
    derivativeValue: derivative?.portfolioValue,
    bondsValue: bonds?.portfolioValue,
    cryptoValue: crypto?.portfolioValue,
    currenciesValue: currencies?.portfolioValue,
    preciousMetalsValue: preciousMetals?.portfolioValue,
  });

  const assetTypeData = useMemo(
    () => [
      {
        key: "holdings" as AssetClass,
        title: t("assetTypeBreakdown.allHoldings.title"),
        value: allHoldings.portfolioValue,
        amount: toCurrency(allHoldings.portfolioValue, currency),
        increase: allHoldings.changeYTD,
        percent: percents.holdings,
        changeAbs: allHoldings.changeTotalAbsolute,
        color: chartColors.holdings,
        noDataText: t("assetTypeBreakdown.allHoldings.noData"),
        hasData: allHoldings.hasData,
      },
      {
        key: "funds" as AssetClass,
        title: t("assetTypeBreakdown.funds.title"),
        value: funds.portfolioValue,
        amount: toCurrency(funds.portfolioValue, currency),
        increase: funds.changeYTD,
        percent: percents.funds,
        changeAbs: funds.changeTotalAbsolute,
        color: chartColors.funds,
        noDataText: t("assetTypeBreakdown.funds.noData"),
        hasData: funds.hasData,
      },
      {
        key: "etfs" as AssetClass,
        title: t("assetTypeBreakdown.etfs.title"),
        value: etfs.portfolioValue,
        amount: toCurrency(etfs.portfolioValue, currency),
        increase: etfs.changeYTD,
        percent: percents.etf,
        changeAbs: etfs.changeTotalAbsolute,
        color: chartColors.etfs,
        noDataText: t("assetTypeBreakdown.etfs.noData"),
        hasData: etfs.hasData,
      },
      {
        key: "derivative" as AssetClass,
        title: t("assetTypeBreakdown.derivative.title"),
        value: derivative.portfolioValue,
        amount: toCurrency(derivative.portfolioValue, currency),
        increase: derivative.changeYTD,
        percent: percents.derivative,
        changeAbs: derivative.changeTotalAbsolute,
        color: chartColors.derivative,
        noDataText: t("assetTypeBreakdown.derivative.noData"),
        hasData: derivative.hasData,
      },
      {
        key: "bonds" as AssetClass,
        title: t("assetTypeBreakdown.bonds.title"),
        value: bonds.portfolioValue,
        amount: toCurrency(bonds.portfolioValue, currency),
        increase: bonds.changeYTD,
        percent: percents.bonds,
        changeAbs: bonds.changeTotalAbsolute,
        color: chartColors.bonds,
        noDataText: t("assetTypeBreakdown.bonds.noData"),
        hasData: bonds.hasData,
      },
      {
        key: "crypto" as AssetClass,
        title: t("assetTypeBreakdown.crypto.title"),
        value: crypto.portfolioValue,
        amount: toCurrency(crypto.portfolioValue, currency),
        increase: crypto.changeYTD,
        percent: percents.crypto,
        changeAbs: crypto.changeTotalAbsolute,
        color: chartColors.crypto,
        noDataText: t("assetTypeBreakdown.crypto.noData"),
        hasData: crypto.hasData,
      },
      {
        key: "currencies" as AssetClass,
        title: t("assetTypeBreakdown.currencies.title"),
        value: currencies.portfolioValue,
        amount: toCurrency(currencies.portfolioValue, currency),
        increase: currencies.changeYTD,
        percent: percents.currencies,
        changeAbs: currencies.changeTotalAbsolute,
        color: chartColors.currency,
        noDataText: t("assetTypeBreakdown.currencies.noData"),
        hasData: currencies.hasData,
      },
      {
        key: "preciousMetals" as AssetClass,
        title: t("assetTypeBreakdown.preciousMetals.title"),
        value: preciousMetals.portfolioValue,
        amount: toCurrency(preciousMetals.portfolioValue, currency),
        increase: preciousMetals.changeYTD,
        percent: percents.preciousMetals,
        changeAbs: preciousMetals.changeTotalAbsolute,
        color: chartColors.metals,
        noDataText: t("assetTypeBreakdown.preciousMetals.noData"),
        hasData: preciousMetals.hasData,
      },
    ],
    [
      allHoldings,
      funds,
      etfs,
      derivative,
      bonds,
      crypto,
      currencies,
      preciousMetals,
      percents,
      chartColors,
      t,
      currency,
    ]
  );

  return assetTypeData;
};
